<template>
  <div>
    <progress-breakdown
      :progress-items="screeningProgressBreakdown"
      :height="height"
      :class="{ ['mb-3']: !hideLegend }"
    />
    <screening-progress-legend
      v-if="!hideLegend"
      :legend-items="screeningProgressItems"
      :show-counts="hasReferences"
    />
  </div>
</template>

<script>
import { Review } from '@/models';
import { ScreeningStatusCategory } from '@/enums';
import ProgressBreakdown from '@/components/Common/ProgressBreakdown';
import ScreeningProgressLegend from '@/components/ReviewSummary/ScreeningProgressLegend';

const ITEMS_EXCLUDED_FOR_SINGLE_REVIEWER = [
  ScreeningStatusCategory.PARTIAL_SCREEN,
  ScreeningStatusCategory.CONFLICT,
];

const CATEGORY_TO_PROPS = {
  [ScreeningStatusCategory.SCREENED]: {
    label: 'Screened',
    tooltipPrefix: 'Screened:',
    color: 'success',
  },
  [ScreeningStatusCategory.PARTIAL_SCREEN]: {
    label: 'Partial Screen',
    tooltipPrefix: 'Partial:',
    color: 'green lighten-4',
  },
  [ScreeningStatusCategory.CONFLICT]: {
    label: 'Conflicts',
    tooltipPrefix: 'Conflicts:',
    color: 'error',
  },
  [ScreeningStatusCategory.UNSCREENED]: {
    label: 'Unscreened',
    tooltipPrefix: 'Pending:',
    color: 'grey lighten-2',
  },
};

const CATEGORY_TO_PROPS_FULL_TEXT_REVIEW = {
  [ScreeningStatusCategory.SCREENED]: {
    label: 'Reviewed',
    color: 'success',
  },
  [ScreeningStatusCategory.PARTIAL_SCREEN]: {
    label: 'Partial Review',
    color: 'green lighten-4',
  },
  [ScreeningStatusCategory.CONFLICT]: {
    label: 'Conflicts',
    color: 'error',
  },
  [ScreeningStatusCategory.UNSCREENED]: {
    label: 'Unreviewed',
    color: 'grey lighten-2',
  },
};

export default {
  name: 'ScreeningProgressBreakdown',
  components: {
    ProgressBreakdown,
    ScreeningProgressLegend,
  },
  props: {
    review: {
      type: Review,
      required: true,
    },
    fullTextReview: {
      type: Boolean,
      default: false,
    },
    hideLegend: {
      type: Boolean,
      default: false,
    },
    height: {
      type: Number,
      default: 20,
    },
  },
  computed: {
    screeningProgressSummary() {
      return this.fullTextReview
        ? this.review.fullTextReviewScreeningProgressSummary
        : this.review.screeningProgressSummary;
    },
    allItems() {
      return [
        this.mergeCountAndProps(
          this.screeningProgressSummary.screenedCount,
          ScreeningStatusCategory.SCREENED
        ),
        this.mergeCountAndProps(
          this.screeningProgressSummary.partiallyScreenedCount,
          ScreeningStatusCategory.PARTIAL_SCREEN
        ),
        this.mergeCountAndProps(
          this.screeningProgressSummary.conflictedScreenCount,
          ScreeningStatusCategory.CONFLICT
        ),
        this.mergeCountAndProps(
          this.screeningProgressSummary.unscreenedCount,
          ScreeningStatusCategory.UNSCREENED
        ),
      ];
    },
    screeningProgressItems() {
      return this.singleReviewer
        ? this.itemsForSingleReviewer
        : this.allItems;
    },
    screeningProgressBreakdown() {
      return this.hasReferences ? this.screeningProgressItems : [];
    },
    itemsForSingleReviewer() {
      return this.allItems.filter(
        (item) =>
          !ITEMS_EXCLUDED_FOR_SINGLE_REVIEWER.includes(item.category)
      );
    },
    // TODO: Replace with referenceCount on Review model
    hasReferences() {
      if (!this.review) return false;
      return (
        this.screeningProgressSummary.unscreenedCount > 0 ||
        this.screeningProgressSummary.partiallyScreenedCount > 0 ||
        this.screeningProgressSummary.conflictsCount > 0 ||
        this.screeningProgressSummary.screenedCount > 0
      );
    },
    singleReviewer() {
      if (this.fullTextReview) {
        return this.review.fullTextReviewVotesRequired === 1;
      }
      return this.review.screeningVotesRequired === 1;
    },
  },
  methods: {
    mergeCountAndProps(count, category) {
      const props = this.fullTextReview
        ? CATEGORY_TO_PROPS_FULL_TEXT_REVIEW
        : CATEGORY_TO_PROPS;
      return { category, count, ...props[category] };
    },
  },
};
</script>
